<template>
  <div class="p-6 mx-auto">
    <PageHeading
      class="mb-6"
      title="Users Documentation"
      description="Learn how to manage user accounts, roles, and permissions in your jukebox system."
      :crumbs="[{ label: 'Documentation' }, { label: 'Users' }]"
    />

    <div class="space-y-6">
      <!-- Overview Section -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Overview</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          The Users page allows administrators to manage all user accounts, assign roles, and
          control access to system features. This page is only accessible to users with admin
          privileges.
        </p>
      </section>

      <!-- User Roles -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">User Roles</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">The system supports four user roles:</p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>
            <strong>Owner:</strong> Full access to all features including user management, events,
            songs, statistics, and settings. The Owner role has the same permissions as Admin.
          </li>
          <li>
            <strong>Admin:</strong> Full access to all features including user management, events,
            songs, and statistics
          </li>
          <li>
            <strong>User:</strong> Can access dashboard, events, and songs management, but cannot
            manage users or view statistics
          </li>
          <li>
            <strong>Helper:</strong> Limited access - can only access the Live Event control panel
            to manage active events
          </li>
        </ul>
      </section>

      <!-- Viewing Users -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Viewing Users</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">The Users table displays:</p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Username and email address</li>
          <li>User role (Owner, Admin, User, or Helper)</li>
          <li>Account status (Active or Disabled)</li>
          <li>Last login date</li>
        </ul>
        <p class="text-gray-600 dark:text-gray-400">
          Use the search bar to find specific users by username or email.
        </p>
      </section>

      <!-- Editing Users -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Editing Users</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">To edit a user account:</p>
        <ol class="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Click the edit icon (pencil) next to the user</li>
          <li>
            Modify the user's information:
            <ul class="list-disc list-inside ml-6 mt-2 space-y-1">
              <li>Username</li>
              <li>Email address</li>
              <li>Role (Owner, Admin, User, or Helper)</li>
            </ul>
          </li>
          <li>Click "Save User" to apply changes</li>
        </ol>
        <p class="text-gray-600 dark:text-gray-400">
          Note: Changing a user's role will immediately affect their access permissions.
        </p>
      </section>

      <!-- Disabling Users -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Disabling Users</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">
          You can disable user accounts to temporarily prevent access:
        </p>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Use the toggle switch in the "Disabled" column to enable/disable a user</li>
          <li>Disabled users cannot log in to the system</li>
          <li>User data is preserved and can be re-enabled at any time</li>
          <li>Only users with Owner or Admin role can disable other users</li>
        </ul>
      </section>

      <!-- Deleting Users -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">Deleting Users</h2>
        <p class="text-gray-600 dark:text-gray-400 mb-4">To permanently delete a user account:</p>
        <ol class="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Click the delete icon (trash) next to the user</li>
          <li>Confirm the deletion in the dialog</li>
        </ol>
        <p class="text-gray-600 dark:text-gray-400">
          <strong>Warning:</strong> Deleting a user is permanent and cannot be undone. All
          associated data will be removed. Consider disabling the user instead if you might need to
          restore access later.
        </p>
      </section>

      <!-- Security Best Practices -->
      <section
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          Security Best Practices
        </h2>
        <ul class="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-400 mb-4">
          <li>Only assign Owner or Admin role to trusted users who need full system access</li>
          <li>Regularly review user accounts and remove inactive or unnecessary accounts</li>
          <li>Use the Helper role for temporary staff who only need to manage live events</li>
          <li>Disable accounts instead of deleting them if you might need to restore access</li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script>
import PageHeading from "../../components/common/PageHeading.vue";

export default {
  name: "UsersDocumentation",
  components: { PageHeading }
};
</script>
